import { createPortal } from "react-dom";
import useMoveSparksModalStore from "../../../store/useMoveSparksModal";
import { PrimaryButton, ThirdButton } from "../../Button/Buttons";
import { useState } from "react";
import Checkbox from "../../Inputs/Checkbox";
import UserAuthStore from "../../../store/userStore";
import { useSpectraSparks } from "../../../api/queryHooks";
import SpectraSelectionDropdown from "../SpectraSelectionDropdown";
import { useMoveChatAPI } from "../../../api/queryHooks";
import { useNavigate, useParams } from "react-router-dom";
import useSessionIdStore from "../../../store/useSessionIdStore";
import useSpectraDeleteModalStore from "../../../store/useSpectraDeleteModal";

const SparkSelection = ({ fromSpace, selectedSparks, setSelectedSparks }) => {
  const user = UserAuthStore((state) => state.user);
  const orgId = UserAuthStore((state) => state.orgId);
  
  const { data: sparksData } = useSpectraSparks(
    fromSpace.space_id,
    orgId,
    user?.uid,
    fromSpace.owner_id,
    { enabled: !!user?.uid && !!orgId && !!fromSpace.space_id }
  );
  const sparks = sparksData?.sparks || [];

  const allSelected =
    sparks.length > 0 &&
    sparks.every((spark) => selectedSparks.includes(spark));

  const toggleSpark = (sprk) => {
    setSelectedSparks((prevSpark) =>
      prevSpark.includes(sprk)
        ? prevSpark.filter((spark) => spark !== sprk)
        : [...prevSpark, sprk]
    );
  };

  const toggleSelectAll = () => {
    if (allSelected) {
      setSelectedSparks([]);
    } else {
      setSelectedSparks(sparks);
    }
  };

  return (
    <div>
      <div className="flex items-center gap-[10px] py-4 border-b-[0.5px] border-edge-border dark:border-edge-hex-aaaaaa80">
        <Checkbox
          className="w-[15px] h-[15px]"
          checked={allSelected}
          onChange={toggleSelectAll}
        />
        <label className="text-[13px] font-semibold text-ink-ink dark:text-ink-white">
          Select All
        </label>
      </div>

      {sparks === undefined ? (
        <div className="min-h-[85px] pt-[15px] text-center text-xs dark:text-ink-hex-aaaaaa text-ink-copy-hover">
          Loading...
        </div>
      ) : (
        <>
          {sparks.map((spark) => (
            <div
              key={spark.uid}
              className="flex items-center gap-[10px] py-4 border-b-[0.5px] border-edge-border dark:border-edge-hex-aaaaaa80 last:border-0"
            >
              <Checkbox
                className="w-[15px] h-[15px]"
                checked={selectedSparks.includes(spark)}
                onChange={() => toggleSpark(spark)}
              />
              <label className="text-[13px] text-ink-ink dark:text-ink-white">
                {spark.title}
              </label>
            </div>
          ))}
        </>
      )}
    </div>
  );
};

const Modal = ({ fromSpace, onClose, onSuccess }) => {
  const [selectedSparks, setSelectedSparks] = useState([]);
  const [moveTo, setMoveTo] = useState(null);
  const user = UserAuthStore((state) => state.user);
  const orgId = UserAuthStore((state) => state.orgId);
  const openDeletePopup = useSpectraDeleteModalStore(
    (state) => state.setIsOpen
  );
  const { id } = useParams();
  const navigate = useNavigate();
  const { mutate, isPending: isLoading } = useMoveChatAPI({
    onSuccess: () => {
      let hasDeletedTheSelected = false;
      for (let sprk of selectedSparks) {
        if (sprk.id === id) {
          hasDeletedTheSelected = true;
        }
      }

      if (hasDeletedTheSelected)
        navigate(`/spectra/${fromSpace.space_id}/chat`);
      onClose();
      openDeletePopup(true, fromSpace);
    },
  });

  const handleClose = () => {
    onClose();
    openDeletePopup(true, fromSpace);
  };

  const handleClickMove = () => {
    mutate({
      new_sid: moveTo?.space_id,
      old_sid: fromSpace.space_id,
      chat_ids: selectedSparks.map((sprk) => sprk.uid),
      org_id: orgId,
      uid: user.uid,
      session_id: useSessionIdStore.getState().session_id,
    });
  };

  return (
    <div
      className="max-w-[480px] w-full bg-surface-dialog py-6 shadow-[0px_1px_4px_#00000029] rounded-2xl flex flex-col"
      onClick={(e) => e.stopPropagation()}
    >
      <div className="flex items-center justify-between px-6">
        <h2 className="text-xl text-ink-ink dark:text-ink-white font-semibold">
          Move Sparks{" "}
          <span className="text-sm text-ink-copy-hover">~ {fromSpace?.title}</span>
        </h2>
      </div>

      <hr className="mt-5 border-edge-border dark:border-edge-hex-aaaaaa80" />
      <div className="max-h-[432px] min-h-[400px] dark:bg-surface-inset overflow-y-auto show-scrollbar flex-grow px-6">
        <SparkSelection
          fromSpace={fromSpace}
          selectedSparks={selectedSparks}
          setSelectedSparks={setSelectedSparks}
        />
      </div>
      <hr className="mb-4 border-edge-border dark:border-edge-hex-aaaaaa80" />
      <div className="px-6 flex flex-wrap text-nowrap gap-3 items-center justify-between">
        <div className="min-w-[200px] max-w-[200px]">
          <SpectraSelectionDropdown
            selected={moveTo}
            onSelect={(space) => setMoveTo(space)}
            fromSpace={fromSpace}
          />
        </div>
        <div className="flex items-center justify-between gap-[15px]">
          <ThirdButton
            onClick={handleClose}
            className="bg-button-neutral-surface border-button-border text-button-neutral-text border-[0.5px] dark:border-0"
          >
            Cancel
          </ThirdButton>
          <PrimaryButton
            className="min-w-[120px]"
            onClick={handleClickMove}
            disabled={selectedSparks.length === 0 || isLoading}
          >
            {isLoading ? " Moving... " : "Move Sparks"}
          </PrimaryButton>
        </div>
      </div>
    </div>
  );
};

const MoveSparksModal = () => {
  const isOpen = useMoveSparksModalStore((state) => state.isOpen);
  const fromSpace = useMoveSparksModalStore((state) => state.fromSpace);
  const setIsOpen = useMoveSparksModalStore((state) => state.setIsOpen);

  const handleOutsideClick = (e) => {
    setIsOpen(false);
  };

  const handleClose = () => {
    setIsOpen(false);
  };

  const onSuccess = () => {
    handleClose();
  };

  if (isOpen)
    return createPortal(
      <div
        className="absolute z-[1000] top-0 left-0 w-screen px-5 h-[100dvh] bg-fill-black/50 flex items-center justify-center"
        onClick={handleOutsideClick}
      >
        <Modal
          fromSpace={fromSpace}
          onClose={handleClose}
          onSuccess={onSuccess}
        />
      </div>,
      document.body
    );
};

export default MoveSparksModal;
